/**
 * Find a merchant from the text a reader typed into page search.
 *
 * The directory is already in memory. This module only ranks it. A name uses
 * the same per-word score as a page. An all-digit query also matches an org id
 * that starts with those digits. Eight is the most the palette shows.
 */
import { pageSearchScore, rankPages, type SearchPage } from './page-search.ts'
import { merchantLabel, type Merchant } from './merchants.ts'

/** How many merchants page search shows for one query. */
export const MERCHANT_SEARCH_LIMIT = 8

/**
 * A longer id that only starts with the query ranks below an exact id.
 * An exact id scores 1, the same as a name that starts with the query.
 */
const ORG_ID_PREFIX_SCORE = 0.85

/**
 * Rank one merchant against the query.
 *
 * An empty query scores 0. `pageSearchScore` scores an empty query as 1, which
 * would offer every merchant before the reader has typed.
 * A digit query uses `startsWith` on the org id, so 12 matches 120 and not 312.
 * A one-digit change is not a match: org ids are exact, and a typo is forgiven
 * only inside a word of letters.
 *
 * @param merchant - One directory row.
 * @param search - What the reader typed.
 * @returns A score from 0 to 1. Zero excludes the merchant.
 */
export function merchantSearchScore(
  merchant: Merchant,
  search: string
): number {
  const query = search.trim()
  if (query === '') return 0
  const nameScore = pageSearchScore(merchantLabel(merchant), query)
  if (!/^\d+$/.test(query)) return nameScore
  const id = String(merchant.orgId)
  if (!id.startsWith(query)) return nameScore
  const idScore = id === query ? 1 : ORG_ID_PREFIX_SCORE
  return Math.max(nameScore, idScore)
}

/**
 * The merchants the palette should list, best match first.
 *
 * Ties break toward the lower org id, so the same query is stable.
 *
 * @param merchants - The directory. Empty while it is still loading.
 * @param search - What the reader typed.
 * @returns At most `MERCHANT_SEARCH_LIMIT` merchants.
 */
export function searchMerchants(
  merchants: readonly Merchant[],
  search: string
): Merchant[] {
  return (
    merchants
      .map(merchant => ({
        merchant,
        score: merchantSearchScore(merchant, search)
      }))
      .filter(item => item.score > 0)
      // `toSorted` is ES2023; this app's TypeScript lib is ES2022. `filter`
      // returned a fresh array, so the sort mutates nothing the caller holds.
      // oxlint-disable-next-line unicorn/no-array-sort
      .sort((a, b) => b.score - a.score || a.merchant.orgId - b.merchant.orgId)
      .slice(0, MERCHANT_SEARCH_LIMIT)
      .map(item => item.merchant)
  )
}

/**
 * The cmdk value for one merchant action.
 *
 * A page value is six fields joined by newlines, and its first field is the
 * title. Three fields starting with `merchant` cannot be one of those, so the
 * palette filter can keep a merchant row without ranking it as a page.
 *
 * @param orgId - The merchant's org id.
 * @param action - Which row this is.
 * @returns The value cmdk should filter.
 */
export function merchantCommandValue(
  orgId: number,
  action: '360' | 'filter'
): string {
  return `merchant\n${orgId}\n${action}`
}

/**
 * Whether cmdk should keep this row as a merchant action.
 *
 * @param value - The item value cmdk is scoring.
 * @returns True when `merchantCommandValue` built it.
 */
export function isMerchantCommandValue(value: string): boolean {
  const parts = value.split('\n')
  return (
    parts.length === 3 &&
    parts[0] === 'merchant' &&
    /^\d+$/.test(parts[1] ?? '') &&
    (parts[2] === '360' || parts[2] === 'filter')
  )
}

/**
 * The same path and search, with `orgIds` set to one merchant.
 *
 * Other parameters stay, in their current order, except `page`: a report
 * returns to its first page when its scope changes. Merchant 360 reads that
 * same parameter: one id in `orgIds` is the merchant the screen shows.
 *
 * @param path - The current path, or the Merchant 360 path.
 * @param search - The current search, with or without a leading `?`.
 * @param orgId - The one merchant to keep.
 * @returns A path cmdk can open.
 */
export function merchantReportHref(
  path: string,
  search: string,
  orgId: number
): string {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  )
  params.set('orgIds', String(orgId))
  params.delete('page')
  const query = params.toString()
  return query === '' ? path : `${path}?${query}`
}

/**
 * Words that join a merchant to the rest of a question, as in "returns for
 * Acme". Page search needs every word to match, so these leave the rest.
 */
const CONNECTING_WORDS: ReadonlySet<string> = new Set([
  'a',
  'an',
  'and',
  'at',
  'by',
  'for',
  'from',
  'in',
  'of',
  'on',
  'the',
  'to',
  'with'
])

/** One unambiguous merchant and the question with its name removed. */
export interface QueryMerchant {
  merchant: Merchant
  rest: string
}

/**
 * Find the longest strong merchant name inside a question without guessing a tie.
 *
 * @param merchants - The loaded directory.
 * @param query - The full question.
 * @param pages - Accessible pages whose whole-query matches take precedence.
 * @returns The merchant and remaining words, or null when no unique match exists.
 */
export function findQueryMerchant(
  merchants: readonly Merchant[],
  query: string,
  pages: readonly SearchPage[] = []
): QueryMerchant | null {
  if (rankPages(pages, query).length > 0) return null
  const words = query.trim().split(/\s+/).filter(Boolean)
  const restMatches = new Map<string, boolean>()
  for (let length = words.length; length > 0; length -= 1) {
    let bestScore = 0
    const best = new Map<number, QueryMerchant>()
    for (let start = 0; start + length <= words.length; start += 1) {
      const run = words.slice(start, start + length).join(' ')
      for (const merchant of merchants) {
        const score = merchantSearchScore(merchant, run)
        if (score < 0.9) continue
        const name = merchantLabel(merchant).toLowerCase()
        const nameWords = name.match(/[\p{L}\p{N}]+/gu) ?? []
        const exactId = /^\d+$/.test(run) && run === String(merchant.orgId)
        // Typo scores can exceed 0.9. Scope needs literal word starts too.
        if (
          !exactId &&
          !run
            .toLowerCase()
            .split(' ')
            .every(
              word =>
                name.startsWith(word) ||
                nameWords.some(nameWord => nameWord.startsWith(word))
            )
        )
          continue
        const rest = [...words.slice(0, start), ...words.slice(start + length)]
          .filter(word => !CONNECTING_WORDS.has(word.toLowerCase()))
          .join(' ')
        let restMatchesPage = restMatches.get(rest)
        if (restMatchesPage === undefined) {
          restMatchesPage = rest === '' || rankPages(pages, rest).length > 0
          restMatches.set(rest, restMatchesPage)
        }
        if (!restMatchesPage) continue
        if (score > bestScore) {
          bestScore = score
          best.clear()
        }
        if (score === bestScore && !best.has(merchant.orgId)) {
          best.set(merchant.orgId, {
            merchant,
            rest
          })
        }
      }
    }
    if (best.size > 0) {
      return best.size === 1 ? [...best.values()][0] : null
    }
  }
  return null
}
